<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { CheckCircle, XCircle } from 'lucide-vue-next';
import { useMessengerFacebook } from '../composables/useMessengerFacebook';
import { type MessengerPage, type MessengerCallbackData } from '../services/api';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { connectPage, error } = useMessengerFacebook();

type State = 'loading' | 'select-page' | 'connecting' | 'success' | 'error';

const state = ref<State>('loading');
const pages = ref<MessengerPage[]>([]);
const callbackData = ref<MessengerCallbackData | null>(null);
const errorMessage = ref('');
const connectedPageName = ref('');

const isInPopup = window.opener !== null;

onMounted(() => {
  const dataParam = route.query.data as string | undefined;
  const errorParam = route.query.error as string | undefined;

  if (errorParam) {
    errorMessage.value =
      errorParam === 'authorization_failed'
        ? t('messenger_callback.error_auth_failed')
        : t('messenger_callback.error_internal');
    state.value = 'error';
    return;
  }

  if (!dataParam) {
    errorMessage.value = t('messenger_callback.error_no_data');
    state.value = 'error';
    return;
  }

  try {
    const decoded = JSON.parse(atob(dataParam)) as MessengerCallbackData;
    callbackData.value = decoded;
    pages.value = decoded.pages;
    state.value = 'select-page';
  } catch {
    errorMessage.value = t('messenger_callback.error_parse_data');
    state.value = 'error';
  }
});

const handleSelectPage = async (page: MessengerPage) => {
  if (!callbackData.value) return;

  state.value = 'connecting';

  const success = await connectPage({
    page_id: page.id,
    page_name: page.name,
    page_access_token: page.access_token,
    organization_id: callbackData.value.organization_id,
    source_id: callbackData.value.source_id,
  });

  if (success) {
    connectedPageName.value = page.name;
    state.value = 'success';

    if (isInPopup) {
      window.opener?.postMessage({ type: 'messenger-connected', pageName: page.name }, '*');
      setTimeout(() => window.close(), 2000);
    } else {
      setTimeout(() => router.push('/messenger'), 2000);
    }
  } else {
    errorMessage.value = error.value || t('messenger_callback.error_connect_page');
    state.value = 'error';
  }
};

const handleClose = () => {
  if (isInPopup) {
    window.close();
  } else {
    router.push('/messenger');
  }
};
</script>

<template>
  <div class="flex min-h-screen bg-[#18181C] items-center justify-center p-6">
    <div
      class="w-full max-w-md bg-[#111113] rounded-3xl shadow-sm border border-[#2A2018] p-8 md:p-10"
    >
      <!-- Loading -->
      <div v-if="state === 'loading'" class="text-center">
        <div
          class="inline-flex items-center justify-center w-20 h-20 bg-[#1E1E23] rounded-3xl mb-6"
        >
          <div
            class="w-10 h-10 border-4 border-[#2A2018] border-t-[#F5F0EA] rounded-full animate-spin"
          ></div>
        </div>
        <h2 class="text-2xl text-[#F5F0EA] mb-2">{{ $t('messenger_callback.loading') }}</h2>
      </div>

      <!-- Select Page -->
      <div v-else-if="state === 'select-page'">
        <div class="flex items-center gap-3 mb-6">
          <div
            class="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center flex-shrink-0"
          >
            <svg class="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
              <path
                d="M12 2C6.477 2 2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.989C18.343 21.129 22 16.99 22 12c0-5.523-4.477-10-10-10z"
              />
            </svg>
          </div>
          <div>
            <h2 class="text-xl text-[#F5F0EA]">{{ $t('messenger_callback.select_page_title') }}</h2>
            <p class="text-sm text-[#9E9080]">
              {{ $t('messenger_callback.select_page_subtitle') }}
            </p>
          </div>
        </div>

        <div class="space-y-2">
          <button
            v-for="page in pages"
            :key="page.id"
            @click="handleSelectPage(page)"
            class="w-full flex items-center gap-3 p-4 border border-[#2A2018] rounded-2xl hover:bg-[#18181C] hover:border-[#2A2018] transition text-left"
          >
            <div
              class="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0"
            >
              <span class="text-[#F5F0EA] text-sm font-medium">{{ page.name[0] }}</span>
            </div>
            <div class="min-w-0">
              <p class="text-[#F5F0EA] font-medium truncate">{{ page.name }}</p>
              <p class="text-xs text-[#524840]">ID: {{ page.id }}</p>
            </div>
          </button>
        </div>

        <button
          @click="handleClose"
          class="mt-4 w-full py-2 text-sm text-[#9E9080] hover:text-[#F5F0EA] transition"
        >
          {{ $t('messenger_callback.cancel') }}
        </button>
      </div>

      <!-- Connecting -->
      <div v-else-if="state === 'connecting'" class="text-center">
        <div class="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-3xl mb-6">
          <div
            class="w-10 h-10 border-4 border-blue-300 border-t-blue-600 rounded-full animate-spin"
          ></div>
        </div>
        <h2 class="text-2xl text-[#F5F0EA] mb-2">{{ $t('messenger_callback.connecting') }}</h2>
        <p class="text-[#9E9080]">{{ $t('messenger_callback.connecting_subtitle') }}</p>
      </div>

      <!-- Success -->
      <div v-else-if="state === 'success'" class="text-center">
        <div
          class="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-3xl mb-6"
        >
          <CheckCircle class="w-10 h-10 text-green-600" />
        </div>
        <h2 class="text-2xl text-[#F5F0EA] mb-2">{{ $t('messenger_callback.success_title') }}</h2>
        <p class="text-[#9E9080] mb-2">
          {{ $t('messenger_callback.success_message', { pageName: connectedPageName }) }}
        </p>
        <p class="text-sm text-[#524840]">
          {{
            isInPopup
              ? $t('messenger_callback.success_closing')
              : $t('messenger_callback.success_redirecting')
          }}
        </p>
      </div>

      <!-- Error -->
      <div v-else class="text-center">
        <div class="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-3xl mb-6">
          <XCircle class="w-10 h-10 text-red-600" />
        </div>
        <h2 class="text-2xl text-[#F5F0EA] mb-2">{{ $t('messenger_callback.error_title') }}</h2>
        <p class="text-[#9E9080] mb-6">{{ errorMessage }}</p>
        <button
          @click="handleClose"
          class="px-6 py-3 bg-[#0A0A0B] text-[#F5F0EA] rounded-2xl hover:bg-[#111113] transition"
        >
          {{ isInPopup ? $t('messenger_callback.close') : $t('messenger_callback.back_to_app') }}
        </button>
      </div>
    </div>
  </div>
</template>
